<template>
  <div class="app" :class="{ 'mobile-open': mobileOpen }">
    <SidebarNav />
    <button
      v-if="mobileOpen"
      class="scrim"
      aria-label="Close menu"
      @click="closeMobile"
    />
    <main class="main">
      <!-- Desktop has no top bar: the left nav already names the current page.
           It returns below 880px, where the drawer needs a hamburger. -->
      <TopBar class="shell-topbar" />
      <slot />
    </main>
    <!-- Cold-load narration. In the shell rather than a view so it covers the
         stretch before any view has mounted (Auth0 restoring the session). -->
    <BootStatus />
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SidebarNav from './SidebarNav.vue'
import TopBar from './TopBar.vue'
import BootStatus from './BootStatus.vue'

const mobileOpen = ref(false)
const route = useRoute()

function openMobile() { mobileOpen.value = true }
function closeMobile() { mobileOpen.value = false }
function toggleMobile() { mobileOpen.value = !mobileOpen.value }

watch(() => route.fullPath, () => { mobileOpen.value = false })

provide('sidebarDrawer', { open: openMobile, close: closeMobile, toggle: toggleMobile, isOpen: mobileOpen })
</script>

<style scoped>
.app {
  display: grid;
  grid-template-columns: 220px 1fr;
  min-height: 100vh;
  /* The sidebar is sticky at 100vh, so on long pages its grid cell runs out
     before the page does. Painting the nav column on the shell itself keeps
     the darker panel continuous all the way down. */
  background: linear-gradient(to right, var(--nav-bg) 0 220px, var(--bg) 220px);
}
.main {
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* viewport-fit=cover lets the page run under the gesture bar; keep the last
     row of content clear of it. Zero where no bar overlays. */
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/* Two-class selector so this beats TopBar's own `.topbar { display: flex }`
   regardless of stylesheet order. */
.main > .shell-topbar { display: none; }
@media (max-width: 880px) {
  .main > .shell-topbar { display: flex; }
}

.scrim {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 40;
  border: 0;
  cursor: pointer;
}

@media (max-width: 880px) {
  /* Drawer mode: the nav floats over the page, so drop the column paint. */
  .app {
    grid-template-columns: 1fr;
    background: var(--bg);
  }
  .app :deep(.sidebar) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 288px;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 0.22s ease-out;
    box-shadow: var(--shadow);
  }
  .app.mobile-open :deep(.sidebar) {
    transform: translateX(0);
  }
}
</style>
